// Admin view: per-driver wallet earnings — today, this week, lifetime, plus a daily bar chart.
// Data source: GET /api/admin/driver-wallets?days=7
import { useEffect, useMemo, useState } from "react";
import api, { extractError } from "@/api/client";
import { X, Wallet, RefreshCw, TrendingUp, Bike, Calendar, Store } from "lucide-react";

function fmtMoney(n) {
  return (Number(n || 0)).toLocaleString("ar-LY", { maximumFractionDigits: 2 });
}
function fmtDay(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("ar-LY", { weekday: "short", day: "numeric", month: "short" });
  } catch { return iso; }
}

export default function DriverWalletsModal({ onClose }) {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [query, setQuery] = useState("");

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const { data: d } = await api.get(`/admin/driver-wallets?days=${days}`);
      setData(d);
    } catch (e) { setErr(extractError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  const filtered = useMemo(() => {
    if (!data?.drivers) return [];
    const q = query.trim().toLowerCase();
    if (!q) return data.drivers;
    return data.drivers.filter((d) =>
      (d.name || "").toLowerCase().includes(q) ||
      (d.phone || "").includes(q) ||
      d.driver_id.includes(q)
    );
  }, [data, query]);

  const chartMax = useMemo(() => {
    if (!data?.drivers) return 1;
    let m = 0;
    for (const dr of data.drivers) {
      for (const d of dr.daily) m = Math.max(m, d.earnings);
    }
    return m || 1;
  }, [data]);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
         onClick={onClose} data-testid="driver-wallets-modal">
      <div onClick={(e) => e.stopPropagation()}
           className="bg-white w-full max-w-5xl rounded-3xl shadow-2xl max-h-[92vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-5 border-b border-[var(--border)] flex items-center justify-between text-white"
             style={{background: "linear-gradient(135deg, #065F46 0%, #0F766E 55%, #0D9488 100%)"}}>
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-white/20 flex items-center justify-center">
              <Wallet size={22}/>
            </div>
            <div>
              <div className="font-black text-lg" style={{fontFamily:"Cairo"}}>محافظ السائقين</div>
              <div className="text-xs opacity-90">إيرادات كل سائق — يومي وأسبوعي</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select value={days} onChange={(e) => setDays(Number(e.target.value))}
                    className="px-2 py-1 rounded-lg bg-white/10 hover:bg-white/20 text-xs font-bold border border-white/30"
                    data-testid="driver-wallets-days">
              <option value={7}>آخر 7 أيام</option>
              <option value={14}>آخر 14 يوم</option>
              <option value={30}>آخر 30 يوم</option>
            </select>
            <button onClick={load} className="w-9 h-9 rounded-lg bg-white/15 hover:bg-white/25 flex items-center justify-center" title="تحديث"
                    data-testid="driver-wallets-refresh">
              <RefreshCw size={15} className={loading ? "animate-spin" : ""}/>
            </button>
            <button onClick={onClose} className="w-9 h-9 rounded-lg bg-white/15 hover:bg-white/25 flex items-center justify-center"
                    data-testid="driver-wallets-close">
              <X size={16}/>
            </button>
          </div>
        </div>

        {/* Totals */}
        {data && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-5 border-b border-[var(--border)] bg-[var(--surface-alt)]">
            <StatCard label="إيرادات اليوم" value={fmtMoney(data.totals.today_earnings) + " د.ل"} sub={`${data.totals.today_deliveries} توصيلة`} tone="emerald" testid="dw-total-today"/>
            <StatCard label={`إيرادات الفترة (${data.days} يوم)`} value={fmtMoney(data.totals.week_earnings) + " د.ل"} sub={`${data.totals.week_deliveries} توصيلة`} tone="teal" testid="dw-total-week"/>
            <StatCard label="عدد السائقين" value={data.drivers.length} sub="على النظام" tone="amber" testid="dw-total-drivers"/>
            <StatCard label="إجمالي (منذ البداية)" value={fmtMoney(data.totals.lifetime_earnings) + " د.ل"} sub={`${data.totals.lifetime_deliveries} توصيلة`} tone="slate" testid="dw-total-lifetime"/>
          </div>
        )}

        {/* Search */}
        <div className="px-5 py-3 border-b border-[var(--border)]">
          <input
            placeholder="بحث عن سائق (اسم أو هاتف)"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full px-3 py-2 rounded-xl border border-[var(--border)] bg-white focus:border-primary-brand focus:outline-none text-sm"
            data-testid="driver-wallets-search"/>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-2">
          {err && <div className="text-sm bg-red-50 text-red-600 px-3 py-2 rounded-lg">{err}</div>}
          {loading && !data && (
            <div className="text-center py-10 text-[var(--text-secondary)]">جاري التحميل...</div>
          )}
          {data && filtered.length === 0 && (
            <div className="text-center py-10 text-[var(--text-secondary)]">
              <Bike size={40} className="mx-auto mb-2 opacity-30"/>
              <div className="text-sm">لا يوجد سائقون مطابقون</div>
            </div>
          )}

          {filtered.map((dr) => {
            const isOpen = expandedId === dr.driver_id;
            return (
              <div key={dr.driver_id} className="rounded-2xl border border-[var(--border)] bg-white talabya-shadow-soft overflow-hidden"
                   data-testid={`dw-row-${dr.driver_id}`}>
                <button
                  onClick={() => setExpandedId(isOpen ? null : dr.driver_id)}
                  className="w-full flex items-center gap-3 p-3 hover:bg-[var(--surface-alt)] text-right">
                  <div className="w-11 h-11 rounded-full bg-primary-light text-primary-brand flex items-center justify-center shrink-0">
                    <Bike size={18}/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-black truncate">{dr.name || "—"}</span>
                      {!dr.active && <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-200 text-gray-600 font-bold">معطّل</span>}
                    </div>
                    <div className="text-[11px] text-[var(--text-secondary)]" dir="ltr">{dr.phone || dr.driver_id.slice(0, 8)}</div>
                  </div>
                  <div className="hidden sm:flex items-center gap-4 text-xs">
                    <MiniStat label="اليوم" value={fmtMoney(dr.today_earnings)} unit="د.ل" tone="emerald"/>
                    <MiniStat label={`${data.days}ي`} value={fmtMoney(dr.week_earnings)} unit="د.ل" tone="teal"/>
                    <MiniStat label="الإجمالي" value={fmtMoney(dr.lifetime_earnings)} unit="د.ل" tone="slate"/>
                  </div>
                  <div className="sm:hidden text-left">
                    <div className="text-xs text-[var(--text-secondary)]">اليوم</div>
                    <div className="font-black text-emerald-700">{fmtMoney(dr.today_earnings)} د.ل</div>
                  </div>
                  <TrendingUp size={16} className={`text-[var(--text-secondary)] transition-transform ${isOpen ? "rotate-180" : ""}`}/>
                </button>

                {isOpen && (
                  <div className="px-3 pb-3 pt-1 bg-[var(--surface-alt)]/40 border-t border-[var(--border)]"
                       data-testid={`dw-row-${dr.driver_id}-details`}>
                    <div className="flex items-center gap-1.5 mb-2 text-xs font-black">
                      <Calendar size={12}/> تفصيل يومي (آخر {data.days} يوم)
                    </div>
                    {/* Daily bars */}
                    <div className="flex items-end gap-1.5 h-24">
                      {dr.daily.map((d) => {
                        const h = Math.max(3, Math.round((d.earnings / chartMax) * 100));
                        const isToday = d.date === data.today;
                        return (
                          <div key={d.date} className="flex-1 flex flex-col items-center gap-1 min-w-0"
                               title={`${d.date}: ${fmtMoney(d.earnings)} د.ل / ${d.deliveries} توصيلة`}>
                            <div className="text-[9px] font-bold text-[var(--text-secondary)] truncate max-w-full">
                              {d.earnings > 0 ? fmtMoney(d.earnings) : ""}
                            </div>
                            <div
                              className={`w-full rounded-t-md transition-all ${isToday ? "bg-primary-brand" : "bg-emerald-500"}`}
                              style={{height: `${h}%`, minHeight: "3px"}}/>
                            <div className="text-[9px] text-[var(--text-secondary)] truncate w-full text-center" dir="ltr">
                              {fmtDay(d.date)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {/* Deliveries table */}
                    <table className="w-full mt-3 text-xs">
                      <thead>
                        <tr className="text-[var(--text-secondary)]">
                          <th className="text-right py-1">اليوم</th>
                          <th className="text-right py-1">التوصيلات</th>
                          <th className="text-right py-1">الإيراد</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dr.daily.map((d) => (
                          <tr key={d.date} className="border-t border-white/60">
                            <td className="py-1 font-bold" dir="ltr">{fmtDay(d.date)}</td>
                            <td className="py-1">{d.deliveries}</td>
                            <td className="py-1 font-black text-emerald-700">{fmtMoney(d.earnings)} د.ل</td>
                          </tr>
                        ))}
                        <tr className="border-t border-white/60 bg-white/70">
                          <td className="py-1 font-black">مجموع الفترة</td>
                          <td className="py-1 font-black">{dr.week_deliveries}</td>
                          <td className="py-1 font-black text-emerald-700">{fmtMoney(dr.week_earnings)} د.ل</td>
                        </tr>
                      </tbody>
                    </table>

                    {/* Per-restaurant breakdown */}
                    <div className="mt-4 pt-3 border-t border-dashed border-[var(--border)]">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-1.5 text-xs font-black">
                          <Store size={12}/> المطاعم التي تم التوصيل منها
                        </div>
                        <div className="text-[10px] font-bold text-[var(--text-secondary)]">
                          {dr.restaurants.length} مطعم · إجمالي {dr.lifetime_deliveries} توصيلة
                        </div>
                      </div>
                      {dr.restaurants.length === 0 ? (
                        <div className="text-[11px] text-[var(--text-secondary)] py-2 text-center">
                          لم يقم بأي توصيل بعد
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                          {dr.restaurants.map((r) => {
                            const pct = dr.lifetime_deliveries
                              ? Math.round((r.deliveries / dr.lifetime_deliveries) * 100)
                              : 0;
                            return (
                              <div key={r.restaurant_id || r.restaurant_name}
                                   className="bg-white rounded-lg border border-[var(--border)] p-2 flex items-center gap-2"
                                   data-testid={`dw-rest-${dr.driver_id}-${r.restaurant_id || "unk"}`}>
                                <div className="w-8 h-8 rounded-lg bg-primary-light text-primary-brand flex items-center justify-center shrink-0">
                                  <Store size={14}/>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="font-bold text-xs truncate">{r.restaurant_name}</div>
                                  <div className="h-1 bg-[var(--surface-alt)] rounded-full mt-1 overflow-hidden">
                                    <div className="h-full bg-primary-brand rounded-full"
                                         style={{width: `${pct}%`}}/>
                                  </div>
                                </div>
                                <div className="text-left shrink-0">
                                  <div className="font-black text-xs text-primary-brand">{r.deliveries}</div>
                                  <div className="text-[9px] text-[var(--text-secondary)]">توصيلة</div>
                                </div>
                                <div className="text-left shrink-0 border-r border-[var(--border)] pr-2">
                                  <div className="font-black text-xs text-emerald-700">{fmtMoney(r.earnings)}</div>
                                  <div className="text-[9px] text-[var(--text-secondary)]">د.ل</div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, tone = "emerald", testid }) {
  const tones = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    teal:    "bg-teal-50 border-teal-200 text-teal-800",
    amber:   "bg-amber-50 border-amber-200 text-amber-800",
    slate:   "bg-slate-50 border-slate-200 text-slate-800",
  };
  return (
    <div className={`rounded-2xl border p-3 ${tones[tone]}`} data-testid={testid}>
      <div className="text-[11px] font-bold opacity-80">{label}</div>
      <div className="mt-1 font-black text-xl" style={{fontFamily:"Cairo"}}>{value}</div>
      {sub && <div className="text-[10px] opacity-70 mt-0.5">{sub}</div>}
    </div>
  );
}

function MiniStat({ label, value, unit, tone }) {
  const t = tone === "emerald" ? "text-emerald-700"
          : tone === "teal"    ? "text-teal-700"
          : "text-slate-700";
  return (
    <div className="text-right">
      <div className="text-[9px] text-[var(--text-secondary)]">{label}</div>
      <div className={`font-black ${t}`}>{value} <span className="text-[9px]">{unit}</span></div>
    </div>
  );
}
